/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CrmDataEnrichmentProcess.module.scss';

type Translate = (key: string) => string;

const getSteps = (t: Translate) => [
  {
    num: '01',
    title: t('Match to the register'),
    body: t(
      'Each record resolves to a single legal entity in a national register, not to a website or a brand. Identifiers first, attributes only after.',
    ),
    path: t('Reg. number → VAT / LEI → domain → name + country + postcode'),
  },
  {
    num: '02',
    title: t('Merge the duplicates'),
    body: t(
      'Records resolving to the same entity are grouped. You choose the survivor and which fields win. Nothing disappears without a merge log.',
    ),
    path: t('Grouped on registration number, never on name similarity'),
  },
  {
    num: '03',
    title: t('Standardise the fields'),
    body: t(
      'Legal name in registry form, address in postal form, country in ISO code, industry in a classification your reporting already runs on.',
    ),
    path: t('ISO 3166 · SIC / NACE / NAICS · registry legal suffixes'),
  },
  {
    num: '04',
    title: t('Complete what is missing'),
    body: t(
      'Firmographics, financials, identifiers and ownership land in the fields you nominate. Populated fields stay untouched unless you say otherwise.',
    ),
    path: t('Field-level mapping, controlled by your admin'),
  },
  {
    num: '05',
    title: t('Keep it current'),
    body: t(
      'A matched record stays connected to its register entry. When a filing changes something, the field changes with it and the change is logged.',
    ),
    path: t('Daily-synced · written back with a timestamp'),
  },
];

export const CrmDataEnrichmentProcess = () => {
  const { t } = useInt();

  const STEPS = getSteps(t);

  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pullRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current, pullRef.current].filter((el): el is HTMLDivElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#EBF2FB" className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock align="left" title={t('Five passes, in this order.')} />
      </div>

      <div className={styles.list}>
        {STEPS.map((step, i) => (
          <div
            key={step.num}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className={cn(styles.row, styles.reveal)}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            <div className={styles.rowNumCol}>
              <span className={styles.rowNum}>{step.num}</span>
              {i < STEPS.length - 1 && <span className={styles.rowConnector} />}
            </div>

            <div className={styles.rowBodyCol}>
              <h3 className={styles.rowTitle}>{step.title}</h3>
              <p className={styles.rowBody}>{step.body}</p>
              <div className={styles.rowPath}>{step.path}</div>
            </div>
          </div>
        ))}
      </div>

      <div ref={pullRef} className={cn(styles.pull, styles.reveal)}>
        <span className={styles.pullMark}>“</span>
        <p className={styles.pullText}>
          {t(
            'The registration number is the whole point. Once a CRM record carries the number the government issued, sales, finance, legal and compliance are finally arguing about the same company.',
          )}
        </p>
      </div>
    </Section>
  );
};
